import { Box, useMantineTheme } from '@mantine/core';
import { forwardRef, useState } from 'react';
import { useReducedMotion } from '../hooks';

export interface HoverLiftProps {
  children: React.ReactNode;
  lift?: number;
  duration?: number;
  shadowColor?: string;
  shadowIntensity?: 'light' | 'medium' | 'strong';
}

const HoverLift = forwardRef<HTMLDivElement, HoverLiftProps>(
  (
    {
      children,
      lift = 8,
      duration = 200,
      shadowColor = 'rgba(0, 0, 0, 0.15)',
      shadowIntensity = 'medium',
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const reducedMotion = useReducedMotion();
    const [isHovered, setIsHovered] = useState(false);

    const shadowSizes = {
      light: { blur: lift * 1.5, spread: 0 },
      medium: { blur: lift * 2, spread: lift / 4 },
      strong: { blur: lift * 3, spread: lift / 2 },
    };

    const { blur, spread } = shadowSizes[shadowIntensity];

    return (
      <Box
        ref={ref}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          transform: isHovered && !reducedMotion ? `translateY(-${lift}px)` : 'translateY(0)',
          boxShadow: isHovered && !reducedMotion
            ? `0 ${lift}px ${blur}px ${spread}px ${shadowColor}`
            : '0 0 0 0 transparent',
          transition: reducedMotion
            ? 'none'
            : `transform ${duration}ms ease-out, box-shadow ${duration}ms ease-out`,
        }}
      >
        {children}
      </Box>
    );
  }
);

HoverLift.displayName = 'HoverLift';

export default HoverLift;
